import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useCallback,
  useRef,
  Suspense,
} from 'react';
import { SUPPORTED_LANGUAGES } from '../utils/translator';
import { useI18n } from '../i18n';
import { useUser } from '../contexts/UserContext';
import { ApiError, fetchApi, parseApiError } from '../utils/api';
import {
  Book,
  ShelfMoveTarget,
  ShelfSlot,
  ShelfUploadTarget,
  UserBookProgress,
} from './shelf3d/types';
import {
  SHELF_THEMES,
  getShelfTheme,
  loadShelfThemePref,
  saveShelfThemePref,
} from './shelf3d/shelfThemes';
import { Spinner } from './Icon';
import { errorDetail, useConfirm, useEscapeLayer, useToast } from './Dialogs';
import './BookShelf.css';

const BookShelf3D = React.lazy(() => import('./shelf3d/BookShelf3D'));

const DEFAULT_TARGET_LANGUAGE = 'zh';
// What the upload form accepts (the server converts mobi/azw3 to epub).
const BOOK_FILE_PATTERN = /\.(epub|mobi|azw3)$/i;

// An upload waiting on credits, kept while the user pays on Stripe (a full
// page load away and back) so the form can reopen where it was. Only the
// key of the copy already in storage travels; the File itself can't.
const PENDING_UPLOAD_KEY = 'ovid_pending_upload';
const PENDING_UPLOAD_MAX_AGE_MS = 60 * 60 * 1000;

interface PendingUpload {
  savedAt: number;
  fileKey: string;
  targetLanguage: string;
  sourceLanguage: string;
  uploadTarget: ShelfUploadTarget | null;
}

/** Read and forget the saved upload, if there is a recent one. */
function takePendingUpload(): PendingUpload | null {
  try {
    const raw = sessionStorage.getItem(PENDING_UPLOAD_KEY);
    sessionStorage.removeItem(PENDING_UPLOAD_KEY);
    if (!raw) return null;
    const pending = JSON.parse(raw) as PendingUpload;
    if (Date.now() - pending.savedAt > PENDING_UPLOAD_MAX_AGE_MS) return null;
    return pending.fileKey ? pending : null;
  } catch {
    return null;
  }
}
const BOOKS_PER_ROW = 8;

function isWebGLAvailable(): boolean {
  try {
    const canvas = document.createElement('canvas');
    return !!(
      window.WebGLRenderingContext &&
      (canvas.getContext('webgl2') || canvas.getContext('webgl'))
    );
  } catch {
    return false;
  }
}

interface BookShelfProps {
  onSelectBook: (bookUuid: string) => void;
}

const DEFAULT_SPINE_RATIO = 1 / 5.3;
const MIN_SPINE_RATIO = 0.04;
const MAX_SPINE_RATIO = 0.35;

// Covers normally land within seconds of a book turning 'ready'; past this
// window assume generation failed and stop refreshing for that book.
const ARTWORK_REFRESH_WINDOW_MS = 2 * 60 * 1000;

function hasCompleteArtwork(book: Book): boolean {
  return !!book.book_cover_img_url && !!book.book_spine_img_url;
}

// Cover generation can fail server-side without flipping status to 'error',
// so missing artwork must never gate opening a book — a 'ready' book without
// cover/spine renders with fallback art and stays readable. isArtworkPending
// only drives the time-bounded artwork refresh in pollProcessingBooks.
function isArtworkPending(book: Book): boolean {
  return book.status !== 'error' && !hasCompleteArtwork(book);
}

function isBookImportPending(book: Book): boolean {
  return book.status === 'processing';
}

function needsTranslationDrive(book: Book): boolean {
  return book.status === 'processing' && !book.language_pair?.endsWith('-none');
}

const BookShelf: React.FC<BookShelfProps> = ({ onSelectBook }) => {
  const { t, locale, setLocale } = useI18n();
  const [confirm, confirmDialog] = useConfirm();
  const [showToast, toast] = useToast();
  const [books, setBooks] = useState<Book[]>([]);
  const [shelfSlots, setShelfSlots] = useState<ShelfSlot[]>([]);
  const [spineRatios, setSpineRatios] = useState<Map<string, number>>(
    new Map()
  );
  const [bookProgressMap, setBookProgressMap] = useState<
    Map<string, UserBookProgress>
  >(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hoveredBook, setHoveredBook] = useState<Book | null>(null);
  const [coverLoaded, setCoverLoaded] = useState(false);
  const [mobileSelectedBook, setMobileSelectedBook] = useState<Book | null>(
    null
  );
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [webglOk] = useState(isWebGLAvailable);
  // One dock menu open at a time — opening another closes the previous.
  const [openDockMenu, setOpenDockMenu] = useState<
    'user' | 'help' | 'theme' | 'signin' | null
  >(null);
  // Email OTP sign-in flow state
  const [signinEmail, setSigninEmail] = useState('');
  const [signinCode, setSigninCode] = useState('');
  const [signinStep, setSigninStep] = useState<'email' | 'code'>('email');
  const [signinBusy, setSigninBusy] = useState(false);
  const [signinError, setSigninError] = useState<string | null>(null);
  const dockRef = useRef<HTMLDivElement>(null);
  // Shelf color theme (3D closet) — owned here so the dock can switch it.
  const [shelfTheme, setShelfTheme] = useState(loadShelfThemePref);
  const handleShelfTheme = useCallback((id: string) => {
    setShelfTheme(id);
    saveShelfThemePref(id);
    setOpenDockMenu(null);
  }, []);

  const signinEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(
    signinEmail.trim()
  );

  const handleSendCode = async () => {
    setSigninBusy(true);
    setSigninError(null);
    try {
      await fetchApi('/api/auth/email/start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: signinEmail.trim() }),
      });
      setSigninStep('code');
    } catch (err) {
      setSigninError(
        err instanceof Error ? err.message : t.auth.sendCodeFailed
      );
    } finally {
      setSigninBusy(false);
    }
  };

  const handleVerifyCode = async () => {
    setSigninBusy(true);
    setSigninError(null);
    try {
      await fetchApi('/api/auth/email/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: signinEmail.trim(),
          code: signinCode.trim(),
        }),
      });
      // Session cookie is set — reload so every context picks it up.
      window.location.reload();
    } catch (err) {
      setSigninError(err instanceof Error ? err.message : t.auth.verifyFailed);
      setSigninBusy(false);
    }
  };
  const wallRef = useRef<HTMLDivElement>(null);
  const [shelfPos, setShelfPos] = useState({
    row1Bottom: '52%',
    row2Bottom: '4%',
    actionsTop: '48%',
    actionsLeft: '250px',
  });
  const [currentPage, setCurrentPage] = useState(0);
  const [pageDir, setPageDir] = useState<'up' | 'down'>('up');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadTarget, setUploadTarget] = useState<ShelfUploadTarget | null>(
    null
  );
  // Buy-credits dialog. `need` is the shortfall when it was opened from the
  // upload form, which stays open underneath.
  const [creditsModal, setCreditsModal] = useState<{
    need: number | null;
  } | null>(null);
  const [buyingPackageId, setBuyingPackageId] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const [uploadError, setUploadError] = useState<{
    message: string;
    required?: number;
    available?: number;
  } | null>(null);
  const [uploadedBookUuid, setUploadedBookUuid] = useState<string | null>(null);
  const toastTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const justUploadedSpineRef = useRef<HTMLDivElement | null>(null);
  const calloutRef = useRef<HTMLDivElement | null>(null);
  const [estimating, setEstimating] = useState(false);
  const [reEstimating, setReEstimating] = useState(false);
  const [skipTranslation, setSkipTranslation] = useState(false);
  const [estimate, setEstimate] = useState<{
    /** null when the form was reopened after paying: only fileKey is left. */
    file: File | null;
    title: string;
    author: string;
    chapters: number;
    characters: number;
    estimatedTokens: number;
    requiredCredits: number;
    availableCredits: number;
    canAfford: boolean;
    fileKey?: string;
    targetLanguage: string;
    detectedSourceLanguage: string;
    sourceLanguage: string;
  } | null>(null);
  const {
    user,
    loading: userLoading,
    login,
    logout,
    credits,
    creditPackages,
    purchaseCredits,
    paymentNotice,
    clearPaymentNotice,
    refreshCredits,
  } = useUser();

  // Each estimate request takes a new number; a response whose number is no
  // longer current (the form was closed, or a newer request went out) is
  // dropped instead of overwriting newer state.
  const estimateRequestRef = useRef(0);
  const estimateAbortRef = useRef<AbortController | null>(null);
  const [fileDragOver, setFileDragOver] = useState(false);

  const openUploadModal = useCallback((target?: ShelfUploadTarget) => {
    setUploadTarget(target ?? null);
    setUploadError(null);
    setEstimate(null);
    setSkipTranslation(false);
    setShowUploadModal(true);
  }, []);

  const closeUploadModal = useCallback(() => {
    estimateRequestRef.current++;
    estimateAbortRef.current?.abort();
    estimateAbortRef.current = null;
    setEstimating(false);
    setReEstimating(false);
    setFileDragOver(false);
    setShowUploadModal(false);
    setUploadTarget(null);
    setUploadError(null);
    setEstimate(null);
    setSkipTranslation(false);
  }, []);

  // Without the stored copy, a form reopened after paying has no file to
  // send again; the user has to pick it again.
  const fileOrChooseAgain = (file: File | null): File => {
    if (!file) throw new Error(t.upload.chooseAgain);
    return file;
  };

  // Escape closes the newest dialog only: the buy dialog before the upload
  // form under it. Neither closes while its request is under way.
  useEscapeLayer(() => {
    if (!uploading) closeUploadModal();
  }, showUploadModal);
  useEscapeLayer(() => {
    if (!buyingPackageId) setCreditsModal(null);
  }, creditsModal !== null);

  const handleBuyPackage = async (packageId: string) => {
    setBuyingPackageId(packageId);
    // Keep the upload this purchase is for, so it can continue afterwards.
    if (showUploadModal && estimate?.fileKey && !skipTranslation) {
      const pending: PendingUpload = {
        savedAt: Date.now(),
        fileKey: estimate.fileKey,
        targetLanguage: estimate.targetLanguage,
        sourceLanguage: estimate.sourceLanguage,
        uploadTarget,
      };
      try {
        sessionStorage.setItem(PENDING_UPLOAD_KEY, JSON.stringify(pending));
      } catch {
        // Without storage the form just won't reopen after paying.
      }
    }
    try {
      // On success this leaves for Stripe; the button stays busy until then.
      await purchaseCredits(packageId);
    } catch (err) {
      try {
        sessionStorage.removeItem(PENDING_UPLOAD_KEY);
      } catch {}
      setBuyingPackageId(null);
      showToast({
        tone: 'error',
        message: t.creditsModal.checkoutFailed,
        detail: errorDetail(err),
      });
    }
  };

  // Coming back to this page with the browser's Back button (from Stripe)
  // can restore it from the page cache, busy button and all.
  useEffect(() => {
    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted) setBuyingPackageId(null);
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  // Reopen an upload that was waiting on credits, from the copy the
  // estimate left in storage, with a fresh estimate for the new balance.
  const resumeUpload = async (pending: PendingUpload) => {
    openUploadModal(pending.uploadTarget ?? undefined);
    setEstimating(true);
    const requestId = ++estimateRequestRef.current;
    try {
      const response = await fetchApi('/api/books/estimate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fileKey: pending.fileKey,
          targetLanguage: pending.targetLanguage,
        }),
      });
      const estimateData = (await response.json()) as {
        title: string;
        author: string;
        chapters: number;
        characters: number;
        estimatedTokens: number;
        requiredCredits: number;
        availableCredits: number;
        canAfford: boolean;
        fileKey?: string;
        detectedSourceLanguage?: string;
      };
      if (requestId !== estimateRequestRef.current) return;
      setEstimate({
        ...estimateData,
        file: null,
        fileKey: estimateData.fileKey || pending.fileKey,
        targetLanguage: pending.targetLanguage,
        detectedSourceLanguage:
          estimateData.detectedSourceLanguage || pending.sourceLanguage,
        sourceLanguage: pending.sourceLanguage,
      });
    } catch (err) {
      if (requestId !== estimateRequestRef.current) return;
      showToast({
        tone: 'error',
        message: t.upload.chooseAgain,
        detail: errorDetail(err),
      });
    } finally {
      if (requestId === estimateRequestRef.current) setEstimating(false);
    }
  };

  // Back from Stripe: say what happened to the payment, then pick the
  // upload back up if one was waiting on these credits.
  useEffect(() => {
    if (!paymentNotice || !user) return;
    clearPaymentNotice();
    if (paymentNotice.kind === 'added') {
      showToast({
        message: paymentNotice.creditsAdded
          ? t.creditsModal.added(paymentNotice.creditsAdded.toLocaleString())
          : t.creditsModal.addedNoAmount,
      });
    } else if (paymentNotice.kind === 'pending') {
      showToast({ message: t.creditsModal.pending });
    }
    const pending = takePendingUpload();
    if (pending) resumeUpload(pending);
  }, [paymentNotice, user]);

  const handleSpineImageLoad = useCallback(
    (bookUuid: string, event: React.SyntheticEvent<HTMLImageElement>) => {
      const img = event.currentTarget;
      const naturalWidth = img.naturalWidth || 0;
      const naturalHeight = img.naturalHeight || 0;
      if (!naturalWidth || !naturalHeight) return;

      const ratio = Math.min(
        MAX_SPINE_RATIO,
        Math.max(MIN_SPINE_RATIO, naturalWidth / naturalHeight)
      );
      setSpineRatios((prev) => {
        const next = new Map(prev);
        if (
          Math.abs((next.get(bookUuid) ?? DEFAULT_SPINE_RATIO) - ratio) < 0.001
        ) {
          return prev;
        }
        next.set(bookUuid, ratio);
        return next;
      });
    },
    []
  );

  const fetchBooks = async () => {
    try {
      const response = await fetch('/api/v2/books');
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const booksData = await response.json();
      const booksList = Array.isArray(booksData) ? (booksData as Book[]) : [];
      setBooks(booksList);
      try {
        const slotsResponse = await fetch('/api/shelf-slots');
        if (slotsResponse.ok) {
          const slotsData = await slotsResponse.json();
          setShelfSlots(Array.isArray(slotsData) ? slotsData : []);
        }
      } catch {
        setShelfSlots([]);
      }
      if (booksData.length > 0 && !hoveredBook) {
        setHoveredBook(booksData[0]);
      }
      setLoading(false);

      // Preload cover images into browser cache. crossOrigin matters: the 3D
      // view loads these same URLs as WebGL textures in CORS mode, and a
      // cached non-CORS response would poison those loads (all covers would
      // silently fall back to generated art).
      booksData.forEach((book) => {
        if (book.book_cover_img_url) {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.src = book.book_cover_img_url;
        }
      });

      // Fetch all progress in a single request
      if (user && booksData.length > 0) {
        try {
          const progressResponse = await fetch('/api/progress');
          if (progressResponse.ok) {
            const data = (await progressResponse.json()) as {
              progress: Record<string, UserBookProgress>;
            };
            const progressMap = new Map<string, UserBookProgress>();
            for (const [uuid, p] of Object.entries(data.progress)) {
              progressMap.set(uuid, p);
            }
            setBookProgressMap(progressMap);
          }
        } catch {
          // Silently skip
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t.errors.fetchBooksFailed);
      console.error('Error fetching books:', err);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBooks();
  }, [user]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // The 3D closet is the shelf view; the classic 2D wall remains only as a
  // fallback for browsers without WebGL.
  const use3D = webglOk;

  // Dynamically compute shelf positions based on background-size: cover scaling
  useEffect(() => {
    if (use3D) return;
    const wall = wallRef.current;
    if (!wall) return;
    const IMG_W = 1248,
      IMG_H = 864;
    // Shelf board positions in image pixel coordinates
    const ROW1_Y = 411; // middle board top (where row-1 books sit)
    const ROW2_Y = 832; // bottom board top (where row-2 books sit)
    const ACT_Y = 418; // actions vertical position
    const ACT_X = 260; // actions horizontal position
    const BOOK_H = 312; // book height in image coordinates
    const update = () => {
      const { width, height } = wall.getBoundingClientRect();
      if (width === 0 || height === 0) return;
      const scale = Math.max(width / IMG_W, height / IMG_H);
      // Set CSS variables for proportional scaling
      wall.style.setProperty('--book-height', `${BOOK_H * scale}px`);
      wall.style.setProperty('--shelf-scale', `${scale}`);
      setShelfPos({
        row1Bottom: `${(1 - (ROW1_Y * scale) / height) * 100}%`,
        row2Bottom: `${(1 - (ROW2_Y * scale) / height) * 100}%`,
        actionsTop: `${((ACT_Y * scale) / height) * 100}%`,
        actionsLeft: `${ACT_X * scale}px`,
      });
    };
    const observer = new ResizeObserver(update);
    observer.observe(wall);
    return () => observer.disconnect();
  }, [use3D]);

  // Move the shelf by one page, clamping to the available range and recording
  // the direction so the slide animation knows which way to move.
  const goPage = useCallback(
    (delta: number) => {
      const safeBooks = Array.isArray(books) ? books : [];
      const overflow = Math.max(
        0,
        safeBooks.filter((b) => !!b.user_id).length - BOOKS_PER_ROW
      );
      const maxPage = Math.ceil(overflow / (BOOKS_PER_ROW * 2));
      setCurrentPage((p) => {
        const next = Math.max(0, Math.min(maxPage, p + delta));
        if (next !== p) setPageDir(delta > 0 ? 'up' : 'down');
        return next;
      });
    },
    [books]
  );

  // Vertical swipe (touch) + trackpad/wheel (desktop) to paginate the shelf.
  // In the 3D closet the wheel is used for zooming instead.
  useEffect(() => {
    if (use3D) return;
    const wall = wallRef.current;
    if (!wall) return;
    let startY = 0;
    let startX = 0;
    let tracking = false;
    const SWIPE_THRESHOLD = 60;
    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length !== 1) return;
      startY = e.touches[0].clientY;
      startX = e.touches[0].clientX;
      tracking = true;
    };
    const onTouchEnd = (e: TouchEvent) => {
      if (!tracking) return;
      tracking = false;
      const t = e.changedTouches[0];
      const dy = t.clientY - startY;
      const dx = t.clientX - startX;
      if (Math.abs(dy) < SWIPE_THRESHOLD) return;
      if (Math.abs(dx) > Math.abs(dy)) return;
      // dy < 0 = swiped up = next page; dy > 0 = swiped down = previous page
      goPage(dy < 0 ? 1 : -1);
    };

    // Trackpad / mouse-wheel: accumulate vertical intent, one page per gesture
    // with a short cooldown so a single swipe doesn't skip multiple pages.
    let accum = 0;
    let cooldown = false;
    let resetTimer: ReturnType<typeof setTimeout> | null = null;
    const WHEEL_THRESHOLD = 80;
    const onWheel = (e: WheelEvent) => {
      if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
      if (cooldown) return;
      accum += e.deltaY;
      if (resetTimer) clearTimeout(resetTimer);
      resetTimer = setTimeout(() => {
        accum = 0;
      }, 200);
      if (Math.abs(accum) >= WHEEL_THRESHOLD) {
        goPage(accum > 0 ? 1 : -1);
        accum = 0;
        cooldown = true;
        setTimeout(() => {
          cooldown = false;
        }, 600);
      }
    };

    wall.addEventListener('touchstart', onTouchStart, { passive: true });
    wall.addEventListener('touchend', onTouchEnd, { passive: true });
    wall.addEventListener('wheel', onWheel, { passive: true });
    return () => {
      wall.removeEventListener('touchstart', onTouchStart);
      wall.removeEventListener('touchend', onTouchEnd);
      wall.removeEventListener('wheel', onWheel);
      if (resetTimer) clearTimeout(resetTimer);
    };
  }, [goPage, use3D]);

  useEffect(() => {
    if (!openDockMenu) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (dockRef.current && !dockRef.current.contains(e.target as Node)) {
        setOpenDockMenu(null);
      }
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpenDockMenu(null);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKey);
    };
  }, [openDockMenu]);

  // Position the upload callout in viewport coords so it can escape
  // .books-grid's overflow:hidden. Recompute on resize and any scroll
  // (the books grid scrolls horizontally).
  //
  // Bottom-row spines sit near the shelf action dock, so the box may need to
  // slide sideways to clear it — the tail tracks the spine
  // independently via --tail-left.
  useLayoutEffect(() => {
    if (!uploadedBookUuid) return;
    const update = () => {
      const spine = justUploadedSpineRef.current;
      const callout = calloutRef.current;
      if (!spine || !callout) return;
      const spineRect = spine.getBoundingClientRect();
      const tw = callout.offsetWidth;
      const th = callout.offsetHeight;
      const margin = 8;
      const viewportW = window.innerWidth;

      const spineCenterX = spineRect.left + spineRect.width / 2;
      const cy = spineRect.top - th - 14;
      let cx = spineCenterX;

      // If the box would collide with .shelf-actions, slide it horizontally
      // to whichever side has more room.
      const actions = document.querySelector('.shelf-actions');
      if (actions) {
        const a = actions.getBoundingClientRect();
        const vOverlap = cy + th > a.top && cy < a.bottom;
        const boxLeft = cx - tw / 2;
        const boxRight = cx + tw / 2;
        const hOverlap =
          boxRight > a.left - margin && boxLeft < a.right + margin;
        if (vOverlap && hOverlap) {
          const roomLeft = a.left;
          const roomRight = viewportW - a.right;
          if (roomLeft >= roomRight) {
            cx = Math.min(spineCenterX, a.left - tw / 2 - margin);
          } else {
            cx = Math.max(spineCenterX, a.right + tw / 2 + margin);
          }
        }
      }

      // Clamp inside viewport so the box never gets clipped off-screen.
      cx = Math.max(tw / 2 + margin, Math.min(cx, viewportW - tw / 2 - margin));

      callout.style.left = `${cx}px`;
      callout.style.top = `${cy}px`;
      // Position the tail at the spine even if the box has been shifted.
      const tailOffsetPx = spineCenterX - cx;
      callout.style.setProperty('--tail-left', `calc(50% + ${tailOffsetPx}px)`);
    };
    const raf = requestAnimationFrame(update);
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [uploadedBookUuid, books]);

  // Drive translation for books that are still processing.
  // Each call to /translate-next translates a chunk of ~25 paragraphs,
  // staying within Cloudflare Workers' 50 subrequest limit.
  const [translationProgress, setTranslationProgress] = useState<
    Map<
      string,
      { phase: string; chaptersCompleted: number; chaptersTotal: number }
    >
  >(new Map());
  const translatingRef = useRef<Set<string>>(new Set());
  const lastTranslationPollRef = useRef<Map<string, number>>(new Map());
  const lastArtworkRefreshRef = useRef(0);
  const artworkPendingSinceRef = useRef<Map<string, number>>(new Map());
  const pollProcessingBooks = useCallback(async () => {
    const booksList = Array.isArray(books) ? books : [];
    const translationBooks = booksList.filter(needsTranslationDrive);
    const now = Date.now();
    const pendingSince = artworkPendingSinceRef.current;
    const artworkPendingBooks = booksList.filter((book) => {
      if (!isArtworkPending(book)) {
        pendingSince.delete(book.uuid);
        return false;
      }
      const since = pendingSince.get(book.uuid) ?? now;
      pendingSince.set(book.uuid, since);
      return now - since < ARTWORK_REFRESH_WINDOW_MS;
    });
    if (translationBooks.length === 0 && artworkPendingBooks.length === 0) {
      return;
    }

    let changed = false;
    for (const book of translationBooks) {
      const lastPoll = lastTranslationPollRef.current.get(book.uuid) ?? 0;
      if (Date.now() - lastPoll < 2800) continue;
      // Don't send a new request while a previous one is still in-flight
      if (translatingRef.current.has(book.uuid)) continue;
      lastTranslationPollRef.current.set(book.uuid, Date.now());
      translatingRef.current.add(book.uuid);

      try {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 55000);
        const res = await fetch(`/api/book/${book.uuid}/translate-next`, {
          method: 'POST',
          signal: controller.signal,
        });
        clearTimeout(timeout);
        if (res.ok) {
          const data = (await res.json()) as {
            done?: boolean;
            error?: string;
            progress?: {
              phase: string;
              chaptersCompleted: number;
              chaptersTotal: number;
            };
          };
          if (data.progress) {
            setTranslationProgress((prev) => {
              const next = new Map(prev);
              next.set(book.uuid, data.progress!);
              return next;
            });
          }
          if (data.done) {
            changed = true;
            setTranslationProgress((prev) => {
              const next = new Map(prev);
              next.delete(book.uuid);
              return next;
            });
          }
        }
      } catch {
        /* ignore, will retry on next poll */
      } finally {
        translatingRef.current.delete(book.uuid);
      }
    }
    const shouldRefreshArtwork =
      artworkPendingBooks.length > 0 &&
      Date.now() - lastArtworkRefreshRef.current > 2800;
    if (changed || shouldRefreshArtwork) {
      if (shouldRefreshArtwork) lastArtworkRefreshRef.current = Date.now();
      await fetchBooks();
    }
  }, [books]);

  // Fetch initial translation progress and keep importing books fresh until
  // both translation and artwork generation have settled.
  const initialProgressFetched = useRef(false);
  useEffect(() => {
    const booksList = Array.isArray(books) ? books : [];
    const translationBooks = booksList.filter(needsTranslationDrive);
    const artworkPendingBooks = booksList.filter(isArtworkPending);
    if (translationBooks.length === 0 && artworkPendingBooks.length === 0) {
      return;
    }

    // Fetch current progress from server only once per processing book
    if (translationBooks.length > 0 && !initialProgressFetched.current) {
      initialProgressFetched.current = true;
      translationBooks.forEach(async (book) => {
        try {
          const res = await fetch(`/api/book/${book.uuid}/status`);
          if (res.ok) {
            const data = (await res.json()) as {
              status: string;
              progress?: {
                phase: string;
                chaptersCompleted: number;
                chaptersTotal: number;
              };
            };
            if (data.progress) {
              setTranslationProgress((prev) => {
                const next = new Map(prev);
                next.set(book.uuid, data.progress!);
                return next;
              });
            }
          }
        } catch {
          /* ignore */
        }
      });
    }

    // Drive translation immediately, then every 3s
    pollProcessingBooks();
    const interval = setInterval(pollProcessingBooks, 3000);
    return () => {
      clearInterval(interval);
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    };
  }, [books, pollProcessingBooks]);

  const handleFileSelect = async (file: File) => {
    if (!user) {
      showToast({ message: t.upload.loginRequired });
      return;
    }

    setEstimating(true);
    setUploadError(null);
    const requestId = ++estimateRequestRef.current;
    const controller = new AbortController();
    estimateAbortRef.current = controller;

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('targetLanguage', DEFAULT_TARGET_LANGUAGE);

      const response = await fetchApi('/api/books/estimate', {
        method: 'POST',
        body: formData,
        signal: controller.signal,
      });

      const estimateData = (await response.json()) as {
        title: string;
        author: string;
        chapters: number;
        characters: number;
        estimatedTokens: number;
        requiredCredits: number;
        availableCredits: number;
        canAfford: boolean;
        fileKey?: string;
        detectedSourceLanguage?: string;
      };

      if (requestId !== estimateRequestRef.current) return;
      const raw = estimateData.detectedSourceLanguage || 'en';
      const detected = SUPPORTED_LANGUAGES[raw] ? raw : 'en';
      setEstimate({
        ...estimateData,
        file,
        targetLanguage: DEFAULT_TARGET_LANGUAGE,
        detectedSourceLanguage: detected,
        sourceLanguage: detected,
      });
    } catch (err) {
      // Cancelled, or superseded: nothing to report.
      if (requestId !== estimateRequestRef.current) return;
      showToast({
        tone: 'error',
        message: t.upload.estimateFailed,
        detail: errorDetail(err),
      });
    } finally {
      if (requestId === estimateRequestRef.current) {
        setEstimating(false);
        estimateAbortRef.current = null;
      }
    }
  };

  // A file picked in the dialog or dropped on the form.
  const acceptBookFile = (file: File) => {
    if (!BOOK_FILE_PATTERN.test(file.name)) {
      showToast({ tone: 'error', message: t.upload.unsupportedFile });
      return;
    }
    handleFileSelect(file);
  };

  const handleChangeTargetLanguage = async (newTarget: string) => {
    if (!estimate || newTarget === estimate.targetLanguage) return;

    // Show the new language straight away; the cost fields read "—" until
    // the new estimate arrives, and a failure puts the old language back.
    const previousTarget = estimate.targetLanguage;
    const requestId = ++estimateRequestRef.current;
    setEstimate((prev) =>
      prev ? { ...prev, targetLanguage: newTarget } : prev
    );
    setReEstimating(true);
    try {
      let response: Response;
      if (estimate.fileKey) {
        response = await fetch('/api/books/estimate', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            fileKey: estimate.fileKey,
            targetLanguage: newTarget,
          }),
        });
        if (response.status === 404) {
          const formData = new FormData();
          formData.append('file', fileOrChooseAgain(estimate.file));
          formData.append('targetLanguage', newTarget);
          response = await fetchApi('/api/books/estimate', {
            method: 'POST',
            body: formData,
          });
        } else if (!response.ok) {
          // Without this a failed estimate was read as a result, and the
          // form showed NaN credits for the new language.
          throw await parseApiError(response);
        }
      } else {
        const formData = new FormData();
        formData.append('file', fileOrChooseAgain(estimate.file));
        formData.append('targetLanguage', newTarget);
        response = await fetchApi('/api/books/estimate', {
          method: 'POST',
          body: formData,
        });
      }

      const estimateData = (await response.json()) as {
        title: string;
        author: string;
        chapters: number;
        characters: number;
        estimatedTokens: number;
        requiredCredits: number;
        availableCredits: number;
        canAfford: boolean;
        fileKey?: string;
        detectedSourceLanguage?: string;
      };

      if (requestId !== estimateRequestRef.current) return;
      setEstimate((prev) =>
        prev
          ? {
              ...prev,
              ...estimateData,
              targetLanguage: newTarget,
              detectedSourceLanguage:
                estimateData.detectedSourceLanguage ||
                prev.detectedSourceLanguage,
            }
          : prev
      );
    } catch (err) {
      if (requestId !== estimateRequestRef.current) return;
      setEstimate((prev) =>
        prev ? { ...prev, targetLanguage: previousTarget } : prev
      );
      showToast({
        tone: 'error',
        message: t.upload.reEstimateFailed,
        detail: errorDetail(err),
      });
    } finally {
      if (requestId === estimateRequestRef.current) setReEstimating(false);
    }
  };

  const handleChangeSourceLanguage = (newSource: string) => {
    setEstimate((prev) =>
      prev ? { ...prev, sourceLanguage: newSource } : prev
    );
  };

  const appendUploadTarget = (formData: FormData) => {
    if (!uploadTarget) return;
    if (uploadTarget.shelfSlotId) {
      formData.append('shelfSlotId', String(uploadTarget.shelfSlotId));
    }
    formData.append('shelfRow', String(uploadTarget.row));
    formData.append('shelfCol', String(uploadTarget.col));
  };

  const handleConfirmUpload = async () => {
    if (!estimate) return;

    setUploading(true);
    setUploadProgress(
      estimate.fileKey ? t.upload.startingTranslation : t.upload.uploading
    );
    setUploadError(null);

    try {
      let response: Response;

      if (estimate.fileKey) {
        // Fast path: file already in R2 from estimate — send fileKey only
        response = await fetchApi('/api/books/upload', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            fileKey: estimate.fileKey,
            targetLanguage: estimate.targetLanguage,
            sourceLanguage: estimate.sourceLanguage,
            skipTranslation,
            shelfSlotId: uploadTarget?.shelfSlotId,
            shelfRow: uploadTarget?.row,
            shelfCol: uploadTarget?.col,
          }),
        });

        // If estimate file not found in R2, silently retry with FormData
        if (response.status === 404) {
          setUploadProgress(t.upload.uploading);
          const formData = new FormData();
          formData.append('file', fileOrChooseAgain(estimate.file));
          formData.append('targetLanguage', estimate.targetLanguage);
          formData.append('sourceLanguage', estimate.sourceLanguage);
          formData.append('skipTranslation', String(skipTranslation));
          appendUploadTarget(formData);

          response = await fetchApi('/api/books/upload', {
            method: 'POST',
            body: formData,
          });
        }
      } else {
        // Fallback: re-upload file via FormData
        const formData = new FormData();
        formData.append('file', fileOrChooseAgain(estimate.file));
        formData.append('targetLanguage', estimate.targetLanguage);
        formData.append('sourceLanguage', estimate.sourceLanguage);
        formData.append('skipTranslation', String(skipTranslation));
        appendUploadTarget(formData);

        response = await fetchApi('/api/books/upload', {
          method: 'POST',
          body: formData,
        });
      }

      if (!response.ok) {
        const errorData = (await response.json()) as {
          error?: string;
          required?: number;
          available?: number;
          message?: string;
          requestId?: string;
        };

        if (response.status === 402) {
          setUploadError({
            message: errorData.message || t.upload.insufficientCredits,
            required: errorData.required,
            available: errorData.available,
          });
          setUploading(false);
          setUploadProgress('');
          // The estimate stays: it is what the form returns to after the
          // user buys credits.
          return;
        }

        throw new ApiError(errorData.error || t.upload.uploadFailed, {
          status: response.status,
          requestId:
            errorData.requestId ||
            response.headers.get('x-request-id') ||
            undefined,
          retryAfter: response.headers.get('retry-after'),
        });
      }

      // Close modal immediately - book will appear on shelf in processing state
      setShowUploadModal(false);
      setUploadTarget(null);
      setUploadProgress('');
      setUploading(false);
      setEstimate(null);
      setSkipTranslation(false);

      // Pin a callout to the new book on the shelf
      const uploadResult = (await response.json()) as { bookUuid?: string };
      if (uploadResult.bookUuid) {
        setUploadedBookUuid(uploadResult.bookUuid);
        if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
        toastTimerRef.current = setTimeout(
          () => setUploadedBookUuid(null),
          12000
        );
      }

      await fetchBooks();
      await refreshCredits();
    } catch (err) {
      setUploadProgress('');
      setUploading(false);
      showToast(
        err instanceof TypeError
          ? { tone: 'error', message: t.upload.networkError }
          : {
              tone: 'error',
              message: t.upload.uploadFailed,
              detail: errorDetail(err),
            }
      );
    }
  };

  const handleDeleteBook = async (bookUuid: string) => {
    const book = books.find((b) => b.uuid === bookUuid);
    const confirmed = await confirm({
      title: t.shelf.removeTitle,
      message: t.shelf.removeMessage(book?.original_title || book?.title || ''),
      confirmLabel: t.common.remove,
      destructive: true,
    });
    if (!confirmed) return;
    try {
      const response = await fetch(`/api/book/${bookUuid}`, {
        method: 'DELETE',
      });
      if (!response.ok) {
        const data = (await response.json()) as { error?: string };
        throw new Error(data.error || t.errors.deleteBookFailed);
      }
      setHoveredBook(null);
      await fetchBooks();
    } catch (err) {
      showToast({
        tone: 'error',
        message: t.errors.deleteBookFailed,
        detail: errorDetail(err),
      });
    }
  };

  const handleSaveSlotLabel = async (
    slotId: number,
    label: string
  ): Promise<boolean> => {
    try {
      const response = await fetch(`/api/shelf-slot/${slotId}/label`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ label }),
      });
      if (!response.ok) {
        const data = (await response.json()) as { error?: string };
        throw new Error(data.error || t.errors.saveLabelFailed);
      }
      // Optimistic: the slots list drives label rendering; books also carry a
      // stale shelf_slot_label, so refetch to keep a cleared label cleared.
      setShelfSlots((prev) =>
        prev.map((s) =>
          s.id === slotId ? { ...s, label: label.trim() || null } : s
        )
      );
      await fetchBooks();
      return true;
    } catch (err) {
      showToast({
        tone: 'error',
        message: t.errors.saveLabelFailed,
        detail: errorDetail(err),
      });
      return false;
    }
  };

  const handleMoveBook = async (
    bookUuid: string,
    target: ShelfMoveTarget,
    insertIndex: number
  ) => {
    const originalBook = books.find((b) => b.uuid === bookUuid);
    if (!originalBook) return;

    // Optimistic reorder: a temporary fractional shelf_position between the
    // target bay's neighbors is enough for layoutBooks() to render the move
    // instantly — fetchBooks() below reconciles with real integer positions.
    setBooks((prev) => {
      const movedIdx = prev.findIndex((b) => b.uuid === bookUuid);
      if (movedIdx === -1) return prev;

      const siblings = prev
        .filter(
          (b) =>
            b.uuid !== bookUuid &&
            b.shelf_row === target.row &&
            b.shelf_col === target.col
        )
        .sort((a, b) => (a.shelf_position ?? 0) - (b.shelf_position ?? 0));
      const before = siblings[insertIndex - 1];
      const after = siblings[insertIndex];
      const beforePos =
        before?.shelf_position ??
        (after ? (after.shelf_position ?? 0) - 2 : -1);
      const afterPos = after?.shelf_position ?? beforePos + 2;

      const next = prev.slice();
      next[movedIdx] = {
        ...next[movedIdx],
        shelf_row: target.row,
        shelf_col: target.col,
        // May be null (brand-new bay) — carrying the old bay's slot id here
        // would poison the layout's per-bay slotId for follow-up drags.
        shelf_slot_id: target.slotId,
        shelf_position: (beforePos + afterPos) / 2,
      };
      return next;
    });

    try {
      const response = await fetch(`/api/book/${bookUuid}/position`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          targetSlotId: target.slotId,
          targetRow: target.row,
          targetCol: target.col,
          insertIndex,
        }),
      });
      if (!response.ok) {
        const data = (await response.json()) as { error?: string };
        throw new Error(data.error || t.errors.moveBookFailed);
      }
    } catch (err) {
      // Revert only the moved book's placement — restoring a whole-array
      // snapshot would clobber poll updates that landed while the PUT flew.
      setBooks((prev) =>
        prev.map((b) =>
          b.uuid === bookUuid
            ? {
                ...b,
                shelf_row: originalBook.shelf_row,
                shelf_col: originalBook.shelf_col,
                shelf_slot_id: originalBook.shelf_slot_id,
                shelf_position: originalBook.shelf_position,
              }
            : b
        )
      );
      showToast({
        tone: 'error',
        message: t.errors.moveBookFailed,
        detail: errorDetail(err),
      });
      return;
    }

    // The move is committed server-side; a failed refresh must not roll it
    // back visually — the optimistic state is already correct enough.
    try {
      await fetchBooks();
    } catch {
      // Next poll/interaction will reconcile.
    }
  };

  if (error) {
    return (
      <div className="bookshelf-container bookshelf-error">
        <div className="bookshelf-error-card" role="alert">
          <h2>{t.shelf.loadFailedTitle}</h2>
          <p>{t.shelf.loadFailedMessage}</p>
          <p className="bookshelf-error-detail">{error}</p>
          <button
            className="bookshelf-error-retry"
            onClick={() => {
              setError(null);
              setLoading(true);
              fetchBooks();
            }}
          >
            {t.errors.tryAgain}
          </button>
        </div>
      </div>
    );
  }

  const safeBooks = Array.isArray(books) ? books : [];
  const publicBooks = safeBooks.filter((b) => !b.user_id);
  const userBooks = safeBooks.filter((b) => !!b.user_id);
  // Page 0 shows publicBooks on row 1 and userBooks[0:8] on row 2.
  // Subsequent pages drop the public row and use both rows for user books.
  const userOverflow = Math.max(0, userBooks.length - BOOKS_PER_ROW);
  const totalPages = 1 + Math.ceil(userOverflow / (BOOKS_PER_ROW * 2));
  const page = Math.min(currentPage, totalPages - 1);

  // Same label regardless of which upload-modal phase is rendering — kept as
  // a single computed value so every phase's dialog root reports it
  // identically to how the old single-wrapper markup did.
  const uploadDialogLabel = estimate
    ? t.upload.dialogConfirm
    : uploadTarget
      ? t.upload.dialogAddHere
      : t.upload.dialogUpload;

  return (
    <div className="bookshelf-container">
      <div
        className={`bookshelf-wall${use3D ? ' closet-mode' : ''}`}
        ref={wallRef}
        style={
          use3D ? undefined : { backgroundImage: 'url(/bookcase_bg.jpeg)' }
        }
      >
        {(() => {
          let row1Books: Book[];
          let row2Books: Book[];
          if (page === 0) {
            row1Books = publicBooks;
            row2Books = userBooks.slice(0, BOOKS_PER_ROW);
          } else {
            const startIdx = BOOKS_PER_ROW + (page - 1) * BOOKS_PER_ROW * 2;
            row1Books = userBooks.slice(startIdx, startIdx + BOOKS_PER_ROW);
            row2Books = userBooks.slice(
              startIdx + BOOKS_PER_ROW,
              startIdx + BOOKS_PER_ROW * 2
            );
          }
          const renderBook = (book: Book) => {
            const isProcessing = isBookImportPending(book);
            const isJustUploaded = book.uuid === uploadedBookUuid;
            return (
              <div
                key={book.uuid}
                ref={isJustUploaded ? justUploadedSpineRef : undefined}
                className={`book-spine-wrapper ${isProcessing ? 'processing' : ''} ${isJustUploaded ? 'just-uploaded' : ''}`}
                onMouseEnter={() => {
                  if (!isMobile) {
                    setCoverLoaded(false);
                    setHoveredBook(book);
                  }
                }}
                onMouseLeave={() => {}}
                onClick={() => {
                  if (isProcessing) return;
                  if (isMobile) {
                    setMobileSelectedBook(book);
                  } else {
                    onSelectBook(book.uuid);
                  }
                }}
              >
                {isJustUploaded && (
                  <div
                    className="upload-callout"
                    ref={calloutRef}
                    onClick={(e) => {
                      e.stopPropagation();
                      setUploadedBookUuid(null);
                      if (toastTimerRef.current)
                        clearTimeout(toastTimerRef.current);
                    }}
                  >
                    <strong>{t.shelf.uploadedTitle}</strong>
                    <span>
                      {book.language_pair?.endsWith('-none')
                        ? t.shelf.uploadedOriginalOnly
                        : t.shelf.uploadedTranslating}
                    </span>
                  </div>
                )}
                <div
                  className="book-spine-container"
                  style={{
                    ['--book-spine-ratio' as any]:
                      spineRatios.get(book.uuid) ?? DEFAULT_SPINE_RATIO,
                  }}
                >
                  {book.book_spine_img_url ? (
                    <img
                      crossOrigin="anonymous"
                      src={book.book_spine_img_url}
                      alt={`${book.title} spine`}
                      className="book-spine-img"
                      onLoad={(event) => handleSpineImageLoad(book.uuid, event)}
                    />
                  ) : (
                    <div
                      className={`book-spine-default ${isProcessing ? 'book-spine-pending' : ''}`}
                      style={
                        isProcessing
                          ? undefined
                          : { backgroundColor: stringToColor(book.title) }
                      }
                    >
                      {!isProcessing && (
                        <span className="spine-title">
                          {book.original_title || book.title}
                        </span>
                      )}
                    </div>
                  )}
                </div>
                {isProcessing && (
                  <div className="book-processing-overlay">
                    <div className="processing-spinner"></div>
                  </div>
                )}
              </div>
            );
          };
          return (
            <div
              className="shelf-content"
              style={{
                opacity: loading ? 0 : 1,
                transition: 'opacity 0.5s ease-in-out',
              }}
            >
              {use3D && (
                <Suspense
                  fallback={
                    <div className="closet-suspense">
                      <div className="processing-spinner" />
                    </div>
                  }
                >
                  <BookShelf3D
                    books={safeBooks}
                    shelfSlots={shelfSlots}
                    shelfTheme={shelfTheme}
                    loading={loading}
                    showProgress={!!user}
                    progressMap={bookProgressMap}
                    translationProgress={translationProgress}
                    currentUserId={user?.id}
                    onRead={onSelectBook}
                    onDelete={handleDeleteBook}
                    onUploadToSlot={user ? openUploadModal : undefined}
                    onMoveBook={user ? handleMoveBook : undefined}
                    onSaveSlotLabel={user ? handleSaveSlotLabel : undefined}
                  />
                </Suspense>
              )}
              {!use3D && row1Books.length > 0 && (
                <div
                  key={`row1-${page}`}
                  className={`books-grid shelf-slide-${pageDir}`}
                  style={{ bottom: shelfPos.row1Bottom }}
                >
                  {row1Books.map(renderBook)}
                </div>
              )}
              <div
                ref={dockRef}
                className={`shelf-actions${use3D ? ' shelf-actions--floating' : ''}`}
                style={
                  use3D
                    ? undefined
                    : { top: shelfPos.actionsTop, left: shelfPos.actionsLeft }
                }
              >
                {userLoading ? null : user ? (
                  <div className="shelf-menu-wrapper">
                    <button
                      className="shelf-avatar-btn"
                      onClick={() =>
                        setOpenDockMenu(openDockMenu === 'user' ? null : 'user')
                      }
                    >
                      {user.picture ? (
                        <img
                          src={user.picture}
                          alt={user.name}
                          className="user-avatar"
                        />
                      ) : (
                        <div className="user-avatar-placeholder">
                          {user.name?.charAt(0) || user.email.charAt(0)}
                        </div>
                      )}
                    </button>
                    {openDockMenu === 'user' && (
                      <div className="shelf-user-dropdown dock-menu">
                        <div className="user-info">
                          <span className="user-name">{user.name}</span>
                          <span className="user-email">{user.email}</span>
                        </div>
                        <div className="dock-menu-divider" />
                        <div className="user-credits">
                          <span className="credits-label">
                            {t.shelf.credits}
                          </span>
                          <span className="credits-amount">
                            {credits?.toLocaleString() ?? '—'}
                          </span>
                        </div>
                        <button
                          className="buy-credits-btn"
                          onClick={() => {
                            setOpenDockMenu(null);
                            setCreditsModal({ need: null });
                          }}
                        >
                          {t.shelf.buyCredits}
                        </button>
                        <div className="dock-menu-divider" />
                        <button className="logout-btn" onClick={logout}>
                          {t.shelf.logout}
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="shelf-menu-wrapper">
                    <button
                      className="shelf-signin-btn"
                      onClick={() =>
                        setOpenDockMenu(
                          openDockMenu === 'signin' ? null : 'signin'
                        )
                      }
                    >
                      <span>{t.shelf.signIn}</span>
                    </button>
                    {openDockMenu === 'signin' && (
                      <div className="shelf-signin-dropdown dock-menu">
                        <button className="signin-google-btn" onClick={login}>
                          <svg viewBox="0 0 24 24" width="16" height="16">
                            <path
                              fill="#4285F4"
                              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                            />
                            <path
                              fill="#34A853"
                              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                            />
                            <path
                              fill="#FBBC05"
                              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                            />
                            <path
                              fill="#EA4335"
                              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                            />
                          </svg>
                          <span>{t.auth.continueWithGoogle}</span>
                        </button>
                        <div className="signin-or">
                          <span>{t.auth.orEmail}</span>
                        </div>
                        {signinStep === 'email' ? (
                          <div className="signin-email-step">
                            <input
                              type="email"
                              className="signin-input"
                              placeholder={t.auth.emailPlaceholder}
                              value={signinEmail}
                              autoComplete="email"
                              onChange={(e) => setSigninEmail(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && signinEmailValid)
                                  handleSendCode();
                              }}
                            />
                            <button
                              className="signin-submit-btn"
                              disabled={signinBusy || !signinEmailValid}
                              onClick={handleSendCode}
                            >
                              {signinBusy ? <Spinner /> : t.auth.sendCode}
                            </button>
                          </div>
                        ) : (
                          <div className="signin-email-step">
                            <div className="signin-code-hint">
                              {t.auth.codeSentLabel}
                            </div>
                            <div
                              className="signin-code-email"
                              title={signinEmail.trim()}
                            >
                              {signinEmail.trim()}
                            </div>
                            <input
                              type="text"
                              inputMode="numeric"
                              maxLength={6}
                              className="signin-input signin-code-input"
                              placeholder={t.auth.codePlaceholder}
                              value={signinCode}
                              autoComplete="one-time-code"
                              autoFocus
                              onChange={(e) =>
                                setSigninCode(e.target.value.replace(/\D/g, ''))
                              }
                              onKeyDown={(e) => {
                                if (
                                  e.key === 'Enter' &&
                                  signinCode.trim().length === 6
                                )
                                  handleVerifyCode();
                              }}
                            />
                            <button
                              className="signin-submit-btn"
                              disabled={
                                signinBusy || signinCode.trim().length !== 6
                              }
                              onClick={handleVerifyCode}
                            >
                              {signinBusy ? (
                                <Spinner />
                              ) : (
                                t.auth.verifyAndSignIn
                              )}
                            </button>
                            <button
                              className="signin-back-btn"
                              onClick={() => {
                                setSigninStep('email');
                                setSigninCode('');
                                setSigninError(null);
                              }}
                            >
                              {t.auth.changeEmail}
                            </button>
                          </div>
                        )}
                        {signinError && (
                          <div className="signin-error">{signinError}</div>
                        )}
                      </div>
                    )}
                  </div>
                )}
                <div className="shelf-help-wrapper shelf-menu-wrapper">
                  <button
                    className="shelf-help-btn"
                    onClick={() =>
                      setOpenDockMenu(openDockMenu === 'help' ? null : 'help')
                    }
                    title={t.shelf.help}
                  >
                    <svg
                      viewBox="0 0 24 24"
                      width="18"
                      height="18"
                      fill="currentColor"
                    >
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 17h-2v-2h2v2zm2.07-7.75l-.9.92C13.45 12.9 13 13.5 13 15h-2v-.5c0-1.1.45-2.1 1.17-2.83l1.24-1.26c.37-.36.59-.86.59-1.41 0-1.1-.9-2-2-2s-2 .9-2 2H8c0-2.21 1.79-4 4-4s4 1.79 4 4c0 .88-.36 1.68-.93 2.25z" />
                    </svg>
                  </button>
                  {openDockMenu === 'help' && (
                    <div className="shelf-help-dropdown dock-menu">
                      <a
                        href="https://github.com/GabrielDrapor/ovid"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="shelf-help-item"
                        onClick={() => setOpenDockMenu(null)}
                      >
                        <svg
                          viewBox="0 0 24 24"
                          width="16"
                          height="16"
                          fill="currentColor"
                        >
                          <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z" />
                        </svg>
                        <span>GitHub</span>
                      </a>
                      <a
                        href="https://discord.gg/DBbB7qSXZf"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="shelf-help-item"
                        onClick={() => setOpenDockMenu(null)}
                      >
                        <svg
                          viewBox="0 0 24 24"
                          width="16"
                          height="16"
                          fill="currentColor"
                        >
                          <path d="M20.317 4.37a19.791 19.791 0 00-4.885-1.515.074.074 0 00-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 00-5.487 0 12.64 12.64 0 00-.617-1.25.077.077 0 00-.079-.037A19.736 19.736 0 003.677 4.37a.07.07 0 00-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 00.031.057 19.9 19.9 0 005.993 3.03.078.078 0 00.084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 00-.041-.106 13.107 13.107 0 01-1.872-.892.077.077 0 01-.008-.128 10.2 10.2 0 00.372-.292.074.074 0 01.077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 01.078.01c.12.098.246.198.373.292a.077.077 0 01-.006.127 12.299 12.299 0 01-1.873.892.077.077 0 00-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 00.084.028 19.839 19.839 0 006.002-3.03.077.077 0 00.032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 00-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
                        </svg>
                        <span>Discord</span>
                      </a>
                      <button
                        type="button"
                        className="shelf-help-item shelf-help-item-btn"
                        onClick={() => {
                          setLocale(locale === 'en' ? 'zh' : 'en');
                          setOpenDockMenu(null);
                        }}
                      >
                        <svg
                          viewBox="0 0 24 24"
                          width="16"
                          height="16"
                          fill="currentColor"
                        >
                          <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zm6.93 6h-2.95c-.32-1.25-.78-2.45-1.38-3.56 1.84.63 3.37 1.91 4.33 3.56zM12 4.04c.83 1.2 1.48 2.53 1.91 3.96h-3.82c.43-1.43 1.08-2.76 1.91-3.96zM4.26 14C4.1 13.36 4 12.69 4 12s.1-1.36.26-2h3.38c-.08.66-.14 1.32-.14 2s.06 1.34.14 2H4.26zm.82 2h2.95c.32 1.25.78 2.45 1.38 3.56-1.84-.63-3.37-1.9-4.33-3.56zm2.95-8H5.08c.96-1.66 2.49-2.93 4.33-3.56C8.81 5.55 8.35 6.75 8.03 8zM12 19.96c-.83-1.2-1.48-2.53-1.91-3.96h3.82c-.43 1.43-1.08 2.76-1.91 3.96zM14.34 14H9.66c-.09-.66-.16-1.32-.16-2s.07-1.35.16-2h4.68c.09.65.16 1.32.16 2s-.07 1.34-.16 2zm.25 5.56c.6-1.11 1.06-2.31 1.38-3.56h2.95c-.96 1.65-2.49 2.93-4.33 3.56zM16.36 14c.08-.66.14-1.32.14-2s-.06-1.34-.14-2h3.38c.16.64.26 1.31.26 2s-.1 1.36-.26 2h-3.38z" />
                        </svg>
                        <span>{locale === 'en' ? '中文' : 'English'}</span>
                      </button>
                    </div>
                  )}
                </div>
                {use3D && (
                  <div className="shelf-menu-wrapper">
                    <button
                      className="shelf-theme-btn"
                      title={t.closet.shelfTheme}
                      aria-label={t.closet.shelfTheme}
                      onClick={() =>
                        setOpenDockMenu(
                          openDockMenu === 'theme' ? null : 'theme'
                        )
                      }
                    >
                      <span
                        className="shelf-theme-dot"
                        style={{
                          background: getShelfTheme(shelfTheme).swatch,
                        }}
                      />
                    </button>
                    {openDockMenu === 'theme' && (
                      <div className="shelf-theme-dropdown dock-menu">
                        {SHELF_THEMES.map((th) => (
                          <button
                            key={th.id}
                            type="button"
                            className={`shelf-theme-option ${
                              shelfTheme === th.id ? 'active' : ''
                            }`}
                            onClick={() => handleShelfTheme(th.id)}
                          >
                            <span
                              className="shelf-theme-dot"
                              style={{ background: th.swatch }}
                            />
                            <span>
                              {t.closet.shelfThemeNames[th.id] || th.id}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {use3D && (
                  <span className="closet-dock-hint">{t.shelf.dockHint}</span>
                )}
              </div>
              {!use3D && row2Books.length > 0 && (
                <div
                  key={`row2-${page}`}
                  className={`books-grid shelf-slide-${pageDir}`}
                  style={{ bottom: shelfPos.row2Bottom }}
                >
                  {row2Books.map(renderBook)}
                </div>
              )}
              {!use3D && totalPages > 1 && (
                <div className="shelf-pagination">
                  <button
                    type="button"
                    className="shelf-page-btn"
                    aria-label={t.shelf.prevPage}
                    onClick={() => goPage(-1)}
                    disabled={page === 0}
                  >
                    ‹
                  </button>
                  <span className="shelf-page-indicator">
                    {page + 1} / {totalPages}
                  </span>
                  <button
                    type="button"
                    className="shelf-page-btn"
                    aria-label={t.shelf.nextPage}
                    onClick={() => goPage(1)}
                    disabled={page >= totalPages - 1}
                  >
                    ›
                  </button>
                </div>
              )}
            </div>
          );
        })()}
      </div>

      {!use3D && (
        <div className="book-preview-sidebar">
          {hoveredBook && (
            <div className="preview-content">
              <div className="preview-cover">
                {hoveredBook.book_cover_img_url ? (
                  <img
                    key={hoveredBook.uuid}
                    src={hoveredBook.book_cover_img_url}
                    alt={hoveredBook.title}
                    className={coverLoaded ? 'cover-loaded' : 'cover-loading'}
                    onLoad={() => setCoverLoaded(true)}
                  />
                ) : (
                  <div className="default-preview-cover">
                    <h3>{hoveredBook.title}</h3>
                    <p>{hoveredBook.author}</p>
                  </div>
                )}
              </div>

              <div className="preview-info">
                <h2>{hoveredBook.original_title || hoveredBook.title}</h2>
                {hoveredBook.original_title &&
                  hoveredBook.title !== hoveredBook.original_title && (
                    <h3 className="translated-title">{hoveredBook.title}</h3>
                  )}
                <p className="author">{t.shelf.byAuthor(hoveredBook.author)}</p>

                {isBookImportPending(hoveredBook) ? (
                  <div className="book-status-processing">
                    <div className="processing-spinner"></div>
                    {(() => {
                      const tp = translationProgress.get(hoveredBook.uuid);
                      if (tp && tp.chaptersTotal > 0) {
                        const pct = Math.round(
                          (tp.chaptersCompleted / tp.chaptersTotal) * 100
                        );
                        return (
                          <>
                            <span>
                              {t.shelf.translatingProgress(
                                tp.chaptersCompleted,
                                tp.chaptersTotal,
                                pct
                              )}
                            </span>
                            <div className="translation-progress-bar">
                              <div
                                className="translation-progress-fill"
                                style={{ width: `${pct}%` }}
                              ></div>
                            </div>
                          </>
                        );
                      }
                      return (
                        <span>
                          {tp?.phase === 'glossary'
                            ? t.shelf.extractingGlossary
                            : needsTranslationDrive(hoveredBook)
                              ? t.shelf.translating
                              : t.shelf.preparingArtwork}
                        </span>
                      );
                    })()}
                    <span className="safe-to-close-hint">
                      {t.shelf.safeToClose}
                    </span>
                  </div>
                ) : hoveredBook.status === 'error' ? (
                  <div className="book-status-error">
                    <span>{t.shelf.translationFailed}</span>
                  </div>
                ) : null}
                {(() => {
                  const progress = bookProgressMap.get(hoveredBook.uuid);
                  const progressPercent = progress?.is_completed
                    ? 100
                    : progress?.reading_progress || 0;
                  const statusText = progress?.is_completed
                    ? t.shelf.completed
                    : progressPercent > 0
                      ? t.shelf.percentRead(progressPercent)
                      : t.shelf.notStarted;
                  return (
                    <>
                      {/* Progress bar - show only for ready books */}
                      {user && !isBookImportPending(hoveredBook) && (
                        <div className="progress-section">
                          <div className="progress-bar">
                            <div
                              className="progress-fill"
                              style={{ width: `${progressPercent}%` }}
                            ></div>
                          </div>
                          <span className="progress-text">{statusText}</span>
                        </div>
                      )}

                      {/* Remove button - only for user-owned books */}
                      {hoveredBook.user_id && (
                        <button
                          className="remove-book-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteBook(hoveredBook.uuid);
                          }}
                          title={t.shelf.removeBook}
                        >
                          <svg
                            viewBox="0 0 24 24"
                            width="16"
                            height="16"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <polyline points="3 6 5 6 21 6" />
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                            <line x1="10" y1="11" x2="10" y2="17" />
                            <line x1="14" y1="11" x2="14" y2="17" />
                          </svg>
                          <span>{t.common.remove}</span>
                        </button>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
          )}

          {/* Auth is now in shelf-actions on the shelf */}
        </div>
      )}

      {/* Mobile floating auth removed - now in shelf-actions */}

      {/* Mobile book bottom sheet */}
      {isMobile && mobileSelectedBook && (
        <>
          <div
            className="mobile-book-sheet-overlay"
            onClick={() => setMobileSelectedBook(null)}
          />
          <div className="mobile-book-sheet">
            <div className="sheet-handle" />
            {mobileSelectedBook.book_cover_img_url && (
              <img
                crossOrigin="anonymous"
                src={mobileSelectedBook.book_cover_img_url}
                alt={mobileSelectedBook.title}
                className="sheet-cover"
              />
            )}
            <h2 className="sheet-title">
              {mobileSelectedBook.original_title || mobileSelectedBook.title}
            </h2>
            {mobileSelectedBook.original_title &&
              mobileSelectedBook.title !==
                mobileSelectedBook.original_title && (
                <p className="sheet-translated-title">
                  {mobileSelectedBook.title}
                </p>
              )}
            <p className="sheet-author">
              {t.shelf.byAuthor(mobileSelectedBook.author)}
            </p>

            {(() => {
              const progress = bookProgressMap.get(mobileSelectedBook.uuid);
              const progressPercent = progress?.is_completed
                ? 100
                : progress?.reading_progress || 0;
              const statusText = progress?.is_completed
                ? t.shelf.completed
                : progressPercent > 0
                  ? t.shelf.percentRead(progressPercent)
                  : t.shelf.notStarted;
              return user && !isBookImportPending(mobileSelectedBook) ? (
                <div className="progress-section">
                  <div className="progress-bar">
                    <div
                      className="progress-fill"
                      style={{ width: `${progressPercent}%` }}
                    ></div>
                  </div>
                  <span className="progress-text">{statusText}</span>
                </div>
              ) : null;
            })()}

            {isBookImportPending(mobileSelectedBook) ? (
              <div className="sheet-status">
                <div
                  className="processing-spinner"
                  style={{ margin: '0 auto 8px' }}
                ></div>
                {(() => {
                  const tp = translationProgress.get(mobileSelectedBook.uuid);
                  if (tp && tp.chaptersTotal > 0) {
                    const pct = Math.round(
                      (tp.chaptersCompleted / tp.chaptersTotal) * 100
                    );
                    return (
                      <span>
                        {t.shelf.translatingProgress(
                          tp.chaptersCompleted,
                          tp.chaptersTotal,
                          pct
                        )}
                      </span>
                    );
                  }
                  return (
                    <span>
                      {needsTranslationDrive(mobileSelectedBook)
                        ? t.shelf.translating
                        : t.shelf.preparingArtwork}
                    </span>
                  );
                })()}
                <span className="safe-to-close-hint">
                  {t.shelf.safeToClose}
                </span>
              </div>
            ) : mobileSelectedBook.status === 'error' ? (
              <div className="sheet-status" style={{ color: '#ff6b6b' }}>
                {t.shelf.translationFailed}
              </div>
            ) : (
              <button
                className="sheet-read-btn"
                onClick={() => {
                  setMobileSelectedBook(null);
                  onSelectBook(mobileSelectedBook.uuid);
                }}
              >
                {t.common.read}
              </button>
            )}

            {mobileSelectedBook.user_id && (
              <button
                className="remove-book-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteBook(mobileSelectedBook.uuid);
                  setMobileSelectedBook(null);
                }}
              >
                <svg
                  viewBox="0 0 24 24"
                  width="16"
                  height="16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                </svg>
                <span>{t.common.remove}</span>
              </button>
            )}
          </div>
        </>
      )}

      {/* Upload Modal — "library forms": Form 1 (Acquisition) covers the
          file-pick / analyzing / uploading / insufficient-credits states;
          Form 2 (Catalogue) is the estimate confirmation once a book has
          been parsed. */}
      {showUploadModal && (
        <div
          className="upload-modal-overlay upload-modal-overlay--form"
          onClick={() =>
            !uploading &&
            !estimating &&
            !uploadError &&
            !estimate &&
            closeUploadModal()
          }
          // A file dropped outside the form would otherwise be opened (or
          // downloaded) by the browser, leaving the app.
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => e.preventDefault()}
        >
          {uploadError ? (
            <div
              className="uf-doc"
              role="dialog"
              aria-label={uploadDialogLabel}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="uf-form-no">
                {t.upload.form1No} <small>· {t.upload.form1Kind}</small>
              </div>
              <p className="uf-entry uf-stamp-text">{uploadError.message}</p>
              {uploadError.required && uploadError.available !== undefined && (
                <>
                  <div className="uf-gap" />
                  <div className="uf-row">
                    <span className="uf-print">{t.upload.requiredField}</span>
                    <span className="uf-entry uf-num">
                      {uploadError.required.toLocaleString()}
                    </span>
                  </div>
                  <div className="uf-row">
                    <span className="uf-print">{t.upload.availableField}</span>
                    <span className="uf-entry uf-num">
                      {uploadError.available.toLocaleString()}
                    </span>
                  </div>
                  <div className="uf-row">
                    <span className="uf-print uf-stamp-text">
                      {t.upload.needField}
                    </span>
                    <span className="uf-entry uf-num uf-stamp-text">
                      {t.upload.moreAmount(
                        (
                          uploadError.required - uploadError.available
                        ).toLocaleString()
                      )}
                    </span>
                  </div>
                </>
              )}
              <div className="uf-actions">
                <button
                  className="uf-btn-primary"
                  onClick={() =>
                    setCreditsModal({
                      need:
                        uploadError.required !== undefined &&
                        uploadError.available !== undefined
                          ? uploadError.required - uploadError.available
                          : null,
                    })
                  }
                >
                  {t.shelf.buyCredits}
                </button>
                <button
                  className="uf-btn-quiet-paper"
                  onClick={closeUploadModal}
                >
                  {t.common.cancel}
                </button>
              </div>
              <div className="uf-hole" />
            </div>
          ) : estimate ? (
            <div
              className="uf-doc"
              role="dialog"
              aria-label={uploadDialogLabel}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="uf-form-no">
                {t.upload.form2No} <small>· {t.upload.form2Kind}</small>
              </div>
              <p className="uf-card-title">{estimate.title}</p>
              <p className="uf-card-author">{estimate.author}</p>

              {/* While uploading the form stays as it is (so it doesn't
                  change height); the main button shows the progress. */}
              <>
                <div className="uf-gap" />
                <div
                  className="uf-mode-selector"
                  role="radiogroup"
                  aria-label={t.upload.modeAria}
                >
                  <label
                    className={`uf-mode-row ${!skipTranslation ? 'checked' : 'dim'}`}
                  >
                    <input
                      type="radio"
                      name="upload-mode"
                      className="uf-sr-only"
                      checked={!skipTranslation}
                      disabled={reEstimating || uploading}
                      onChange={() => setSkipTranslation(false)}
                    />
                    <span className="uf-box" aria-hidden="true" />
                    <span className="uf-entry">
                      {t.upload.translateThisBook}
                    </span>
                    <span className="uf-mode-note uf-print">
                      {t.upload.bilingualNote}
                    </span>
                  </label>
                  <label
                    className={`uf-mode-row ${skipTranslation ? 'checked' : 'dim'}`}
                  >
                    <input
                      type="radio"
                      name="upload-mode"
                      className="uf-sr-only"
                      checked={skipTranslation}
                      disabled={reEstimating || uploading}
                      onChange={() => setSkipTranslation(true)}
                    />
                    <span className="uf-box" aria-hidden="true" />
                    <span className="uf-entry">
                      {t.upload.importOriginalOnly}
                    </span>
                    <span className="uf-mode-note uf-print">
                      {t.upload.freeNote}
                    </span>
                  </label>
                </div>
                {!skipTranslation && (
                  <div className="uf-row uf-lang-line">
                    <span className="uf-print">{t.upload.languagesField}</span>
                    <span className="uf-lang-fields">
                      <select
                        id="estimate-source-language"
                        aria-label={t.upload.sourceLanguage}
                        value={estimate.sourceLanguage}
                        disabled={reEstimating || uploading}
                        onChange={(e) =>
                          handleChangeSourceLanguage(e.target.value)
                        }
                      >
                        {Object.entries(SUPPORTED_LANGUAGES).map(
                          ([code, label]) => (
                            <option key={code} value={code}>
                              {t.languageNames[code] || label}
                            </option>
                          )
                        )}
                      </select>
                      <span className="uf-print uf-arrow">→</span>
                      <select
                        id="estimate-target-language"
                        aria-label={t.upload.targetLanguage}
                        value={estimate.targetLanguage}
                        disabled={reEstimating || uploading}
                        onChange={(e) =>
                          handleChangeTargetLanguage(e.target.value)
                        }
                      >
                        {Object.entries(SUPPORTED_LANGUAGES).map(
                          ([code, label]) => (
                            <option key={code} value={code}>
                              {t.languageNames[code] || label}
                            </option>
                          )
                        )}
                      </select>
                    </span>
                  </div>
                )}

                {!skipTranslation && (
                  <>
                    <div className="uf-gap" />
                    <div className="uf-fee-zone">
                      <div className="uf-fee-lines">
                        <div className="uf-row">
                          <span className="uf-print">
                            {t.upload.balanceField}
                          </span>
                          <span className="uf-entry uf-num">
                            {estimate.availableCredits.toLocaleString()}
                          </span>
                        </div>
                        <div className="uf-row">
                          <span className="uf-print">
                            {t.upload.afterField}
                          </span>
                          <span className="uf-entry uf-num">
                            {reEstimating
                              ? '—'
                              : (
                                  estimate.availableCredits -
                                  estimate.requiredCredits
                                ).toLocaleString()}
                          </span>
                        </div>
                        {!estimate.canAfford && !reEstimating && (
                          <p className="uf-print uf-stamp-text uf-need-more">
                            {t.upload.needMore(
                              (
                                estimate.requiredCredits -
                                estimate.availableCredits
                              ).toLocaleString()
                            )}
                          </p>
                        )}
                      </div>
                      <div className="uf-stamp">
                        <div className="uf-stamp-num">
                          {reEstimating
                            ? '…'
                            : estimate.requiredCredits.toLocaleString()}
                        </div>
                        <div className="uf-stamp-unit">
                          {t.upload.creditsUnit}
                        </div>
                      </div>
                    </div>
                  </>
                )}
                {!skipTranslation &&
                  estimate.sourceLanguage === estimate.targetLanguage && (
                    <p className="uf-print uf-stamp-text uf-lang-warning">
                      {t.upload.sameLanguageWarning}
                    </p>
                  )}
                <div className="uf-actions">
                  {uploading ? (
                    <button className="uf-btn-primary" disabled>
                      <span className="uf-btn-busy">
                        <Spinner />
                        {uploadProgress}
                      </span>
                    </button>
                  ) : skipTranslation ? (
                    <button
                      className="uf-btn-primary"
                      onClick={handleConfirmUpload}
                      disabled={reEstimating}
                    >
                      {t.upload.importWithoutTranslation}
                    </button>
                  ) : estimate.canAfford ? (
                    <button
                      className="uf-btn-primary"
                      onClick={handleConfirmUpload}
                      disabled={
                        reEstimating ||
                        estimate.sourceLanguage === estimate.targetLanguage
                      }
                    >
                      {t.upload.confirmTranslate}
                    </button>
                  ) : (
                    <button
                      className="uf-btn-primary"
                      onClick={() =>
                        setCreditsModal({
                          need:
                            estimate.requiredCredits -
                            estimate.availableCredits,
                        })
                      }
                      disabled={reEstimating}
                    >
                      {t.shelf.buyCredits}
                    </button>
                  )}
                  {/* Cancel closes the form, like every other Cancel
                        here; pick another file by opening it again. */}
                  <button
                    className="uf-btn-quiet-paper"
                    onClick={closeUploadModal}
                    disabled={uploading}
                  >
                    {t.common.cancel}
                  </button>
                </div>
              </>
              <div className="uf-hole" />
            </div>
          ) : (
            /* Form 1. Picking, analyzing and uploading share one layout, so
               the paper keeps its height between them. */
            <div
              className="uf-doc"
              role="dialog"
              aria-label={uploadDialogLabel}
              onClick={(e) => e.stopPropagation()}
              onDragOver={(e) => {
                e.preventDefault();
                if (estimating || uploading) {
                  e.dataTransfer.dropEffect = 'none';
                  return;
                }
                e.dataTransfer.dropEffect = 'copy';
                setFileDragOver(true);
              }}
              onDragLeave={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget as Node | null))
                  setFileDragOver(false);
              }}
              onDrop={(e) => {
                e.preventDefault();
                setFileDragOver(false);
                const file = e.dataTransfer.files?.[0];
                if (file && !estimating && !uploading) acceptBookFile(file);
              }}
            >
              <div className="uf-form-no">
                {t.upload.form1No} <small>· {t.upload.form1Kind}</small>
              </div>
              {(() => {
                const rows = (
                  <>
                    {uploadTarget && (
                      <div className="uf-row">
                        <span className="uf-print">{t.upload.shelfField}</span>
                        <span className="uf-entry">
                          {uploadTarget.label ||
                            (uploadTarget.position
                              ? t.upload.slotPosition(
                                  uploadTarget.position.row,
                                  uploadTarget.position.col
                                )
                              : t.upload.selectedShelf)}
                        </span>
                      </div>
                    )}
                    <div className="uf-row">
                      <span className="uf-print">{t.upload.creditsUnit}</span>
                      <span className="uf-entry uf-num">
                        {credits?.toLocaleString() ?? '—'}
                      </span>
                    </div>
                  </>
                );
                const footer = (
                  <>
                    <div
                      className="uf-row"
                      style={{ justifyContent: 'center' }}
                    >
                      <span className="uf-print">
                        {t.upload.acceptedFormats}
                      </span>
                    </div>
                    <div
                      className="uf-row"
                      style={{ justifyContent: 'center' }}
                    >
                      <span className="uf-print">
                        {t.upload.languagesNextForm}
                      </span>
                    </div>
                  </>
                );
                return estimating || uploading ? (
                  <div className="uf-doc-affix uf-doc-affix-busy">
                    {rows}
                    <div className="uf-affix-status" role="status">
                      <div className="uf-spinner" />
                      <p className="uf-print">
                        {estimating ? t.upload.analyzing : uploadProgress}
                      </p>
                    </div>
                    {footer}
                  </div>
                ) : (
                  <label
                    htmlFor="epub-file-input"
                    className={`uf-doc-affix${
                      fileDragOver ? ' is-dragover' : ''
                    }`}
                  >
                    {rows}
                    <input
                      type="file"
                      accept=".epub,.mobi,.azw3"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        // Let the same file be picked again after a cancel.
                        e.target.value = '';
                        if (file) acceptBookFile(file);
                      }}
                      id="epub-file-input"
                      style={{ display: 'none' }}
                    />
                    <div className="uf-affix-box">
                      <div className="uf-plus" aria-hidden="true">
                        +
                      </div>
                      <div className="uf-affix-note">{t.upload.affixHere}</div>
                    </div>
                    {footer}
                  </label>
                );
              })()}
              <div className="uf-actions">
                <button
                  className="uf-btn-quiet-paper"
                  style={{ flex: 1 }}
                  onClick={closeUploadModal}
                  disabled={uploading}
                >
                  {t.common.cancel}
                </button>
              </div>
              <div className="uf-hole" />
            </div>
          )}
        </div>
      )}

      {/* Credits Purchase Modal — on top of the upload form when opened
          from it, which stays as it was underneath. */}
      {creditsModal && (
        <div
          className="upload-modal-overlay"
          onClick={() => !buyingPackageId && setCreditsModal(null)}
        >
          <div
            className="upload-modal-content credits-modal"
            role="dialog"
            aria-label={t.creditsModal.dialogAria}
            onClick={(e) => e.stopPropagation()}
          >
            <h2>{t.creditsModal.title}</h2>
            <p className="credits-balance">
              {t.creditsModal.balancePrefix}{' '}
              <strong>{credits?.toLocaleString() ?? '—'}</strong>{' '}
              {t.creditsModal.creditsSuffix}
            </p>
            {creditsModal.need !== null && creditsModal.need > 0 && (
              <p className="credits-need">
                {t.creditsModal.needMore(creditsModal.need.toLocaleString())}
              </p>
            )}
            <div className="credit-packages">
              {creditPackages.map((pkg) => {
                const busy = buyingPackageId === pkg.id;
                // A package that wouldn't cover this book is faded.
                const fallsShort =
                  creditsModal.need !== null && pkg.credits < creditsModal.need;
                return (
                  <button
                    key={pkg.id}
                    className={`credit-package${
                      fallsShort ? ' credit-package--short' : ''
                    }`}
                    onClick={() => handleBuyPackage(pkg.id)}
                    disabled={buyingPackageId !== null}
                    aria-busy={busy}
                  >
                    <span className="package-credits">
                      {pkg.credits.toLocaleString()}
                    </span>
                    <span className="package-label">
                      {t.creditsModal.packageUnit}
                    </span>
                    <span className="package-price">
                      {busy ? <Spinner /> : `$${(pkg.price / 100).toFixed(2)}`}
                    </span>
                  </button>
                );
              })}
            </div>
            <p className="credits-info">{t.creditsModal.info}</p>
            <div className="uf-actions">
              <button
                className="uf-btn-quiet"
                style={{ flex: 1 }}
                onClick={() => setCreditsModal(null)}
                disabled={buyingPackageId !== null}
              >
                {t.common.cancel}
              </button>
            </div>
          </div>
        </div>
      )}
      {confirmDialog}
      {toast}
    </div>
  );
};

// Helper to generate consistent colors for default spines
function stringToColor(str: string) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const c = (hash & 0x00ffffff).toString(16).toUpperCase();
  return '#' + '00000'.substring(0, 6 - c.length) + c;
}

export default BookShelf;
